import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ContactForm from '../FormSection/Form';
import ContactLayoutSkeleton from './ContactLayoutSkeleton';
import ErrorState from './../../ErrorState/ErrorState';
import { useCachedFetch } from '../../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../../AnimatedOnScroll';
import './ContactLayout.scss';

const ContactLayout = () => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    
    const { data: contactData, loading: dataLoading, error: dataError, refetch } = useCachedFetch('/data/locales/contact.json');

    const handleRetry = useCallback(() => {
        refetch();
    }, [refetch]);

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (dataLoading) return;

        if (dataError) {
            setHasError(true);
            setIsLoading(false);
            return;
        }

        const contactDataFromJson = contactData?.ContactData?.ContactPage?.[currentLang] || contactData?.ContactData?.ContactPage?.en;
        
        if (contactDataFromJson && 
            contactDataFromJson.contentAccent && 
            contactDataFromJson.contentTitle && 
            contactDataFromJson.contentSubtitle && 
            contactDataFromJson.contentEmailUs && 
            contactDataFromJson.buttonEmailUsText && 
            contactDataFromJson.buttonEmailUsLink && 
            contactDataFromJson.buttonMediaText && 
            contactDataFromJson.buttonMediaLink && 
            contactDataFromJson.contentPresentation && 
            contactDataFromJson.buttonPresentationText && 
            contactDataFromJson.buttonPresentationLink && 
            contactDataFromJson.mainTitle && 
            contactDataFromJson.mainSubtitle) {
            setHasError(false);
        } else {
            setHasError(true);
        }

        setIsLoading(false);
    }, [dataLoading, dataError, contactData, currentLang]);

    const contactLayoutData = useMemo(() => {
        return contactData?.ContactData?.ContactPage?.[currentLang] || contactData?.ContactData?.ContactPage?.en || {};
    }, [contactData, currentLang]);

    if (isLoading) {
        return <ContactLayoutSkeleton type="contact" />;
    }

    if (hasError || !contactLayoutData) {
        return <ErrorState onRetry={handleRetry} />;
    }

    return (
        <div className="contact__wrapper">
            <div className="left__side__services__section">
                <div className="contact__content">
                    <AnimatedOnScroll animation="fade-up" delay={0}>
                    <p className="services__accent">{contactLayoutData.contentAccent}</p>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={100}>
                    <h4 className="services__title">{contactLayoutData.contentTitle}</h4>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={200}>
                    <h5 className="services__subtitle">{contactLayoutData.contentSubtitle}</h5>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={300}>
                    <h6 className="services__subtitle">{contactLayoutData.contentEmailUs}</h6>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={400}>
                    <a href={contactLayoutData.buttonEmailUsLink} className="services__button">
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                        {contactLayoutData.buttonEmailUsText}
                    </a>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={400}>
                    <a href={contactLayoutData.buttonMediaLink} className="services__button btn__media">
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                        {contactLayoutData.buttonMediaText}
                    </a>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={500}>
                    <h6 className="services__subtitle__unique">{contactLayoutData.contentPresentation}</h6>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={500}>
                    <a
                        href={contactLayoutData.buttonPresentationLink} 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="services__button--primary"
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                        {contactLayoutData.buttonPresentationText}
                    </a>
                    </AnimatedOnScroll>
                </div>
            </div>
            <div className="right__side__contact__section">
                <AnimatedOnScroll animation="fade-down" delay={0}>
                <div className='contact__section__wrapper__title'>
                    <h5 className='contact__section__title'>{contactLayoutData.mainTitle}</h5>
                    <p className="contact__section__subtitle">{contactLayoutData.mainSubtitle}</p>
                </div>
                </AnimatedOnScroll>
                <ContactForm />
            </div>
        </div>
    );
};

export default React.memo(ContactLayout);